import React, { useEffect, useMemo, useRef, useState } from 'react';
import { arrayUnion, collection, doc, getDoc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { db } from '../../../../shared/storage/firebase';
import type { User } from '../../../../types';
import type { Squad } from '../../../../types';
import SearchableSelect from '../shared/SearchableSelect';
import { syncSquadCalendarFromDetails } from '../../../squads/utils/calendarSync';
import ConfirmationModal from '../../../squads/components/ConfirmationModal';
import { analyzeLeadTeacherAssignment, changeSquadLeadTeacher } from '../../../squads/services/leadTeacherAssignment';

function safeTrim(v: any): string {
  return String(v ?? '').trim();
}

export default function LeadTeacherAssignmentLogistic(props: {
  squadId: string;
  currentValue: string;
  currentUserId: string;
}): React.ReactElement {
  const { squadId, currentValue } = props;

  const [selectedTeacherId, setSelectedTeacherId] = useState<string>(() => safeTrim(currentValue));
  const [teachers, setTeachers] = useState<User[]>([]);
  const [loadingTeachers, setLoadingTeachers] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const hydratedFromSquadRef = useRef(false);
  const confirmResolverRef = useRef<null | ((v: boolean) => void)>(null);
  const [confirmState, setConfirmState] = useState<{
    open: boolean;
    title: string;
    message: string;
    confirmText: string;
  }>({ open: false, title: '', message: '', confirmText: 'Confirm' });

  const promptConfirm = (opts: { title: string; message: string; confirmText: string }): Promise<boolean> => {
    return new Promise((resolve) => {
      confirmResolverRef.current = resolve;
      setConfirmState({ open: true, ...opts });
    });
  };

  useEffect(() => {
    const loadTeachers = async () => {
      try {
        const teachersSnap = await getDocs(query(collection(db, 'users'), where('role', '==', 'teacher')));
        const teachersData = teachersSnap.docs.map(d => ({ id: d.id, ...d.data() } as User));
        setTeachers(teachersData.sort((a, b) => (a.displayName || '').localeCompare(b.displayName || '')));
      } catch (e: any) {
        console.error('Failed to load teachers:', e);
      } finally {
        setLoadingTeachers(false);
      }
    };
    void loadTeachers();
  }, []);

  const selectedTeacher = useMemo(() => {
    return teachers.find(t => t.id === selectedTeacherId) || null;
  }, [teachers, selectedTeacherId]);

  const teacherOptions = useMemo(() => {
    return teachers.map(t => ({
      id: t.id,
      label: `${t.displayName || 'Unnamed'} (${t.email || 'no email'})`,
    }));
  }, [teachers]);

  useEffect(() => {
    const cur = safeTrim(currentValue);
    if (cur) {
      hydratedFromSquadRef.current = true;
      setSelectedTeacherId(cur);
      return;
    }

    // Back-compat: older squads may only have `regularTeacher` set (not `leadTeacherId`).
    // If the logistic value is blank, hydrate the picker from the squad doc once.
    if (!squadId) return;
    if (hydratedFromSquadRef.current) return;

    let alive = true;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'squads', squadId));
        if (!alive) return;
        if (!snap.exists()) return;
        const data: any = snap.data() || {};
        const fallback = safeTrim(data.leadTeacherId) || safeTrim(data.regularTeacher) || '';
        if (!fallback) return;
        hydratedFromSquadRef.current = true;
        setSelectedTeacherId((prev) => (safeTrim(prev) ? prev : fallback));
      } catch (e) {
        // ignore (best-effort)
      }
    })();

    return () => {
      alive = false;
    };
  }, [currentValue]);

  const saveSelection = async () => {
    if (!squadId) return;
    setSaving(true);
    setStatus(null);
    try {
      const squadRef = doc(db, 'squads', squadId);
      const snap = await getDoc(squadRef);
      const existing = (snap.exists() ? ({ ...(snap.data() as any), id: squadId } as Squad & Record<string, any>) : null);

      const newTeacherId = safeTrim(selectedTeacherId) || null;
      const oldRegularTeacher = safeTrim((existing as any)?.regularTeacher) || null;

      // Mirror Squad Details behavior: warn if teacher has no availability templates.
      let analysis: any = null;
      if (existing && newTeacherId && newTeacherId !== oldRegularTeacher) {
        analysis = await analyzeLeadTeacherAssignment({ squad: existing as any, newTeacherId });

        if (!analysis.hasAnyWeeklyAvailabilityTemplate) {
          const ok = await promptConfirm({
            title: 'Teacher has no availability schedule',
            message:
              'This teacher has not entered any weekly availability yet.\n\n'
              + 'Assign them as lead teacher anyway?',
            confirmText: 'Assign anyway',
          });
          if (!ok) return;
        }

      }

      // Keep both fields in sync:
      // - Ops Flows uses `leadTeacherId` (canonical long-term)
      // - Admin/squad details + calendar sync uses `regularTeacher` today
      const payload: any = {
        leadTeacherId: newTeacherId,
        regularTeacher: newTeacherId,
      };
      if (newTeacherId) {
        payload.teacherIds = arrayUnion(newTeacherId);
      }

      if (newTeacherId !== oldRegularTeacher) {
        await changeSquadLeadTeacher({ squadId, oldTeacherId: oldRegularTeacher || null, newTeacherId: newTeacherId || null });
      } else {
        await updateDoc(squadRef, payload);
      }

      // Ensure the calendar (squadEvents) stays in sync with lead teacher changes
      // exactly like the squad details save path.
      if (existing) {
        const updatedSquad: any = { ...existing, ...payload, id: squadId };
        await syncSquadCalendarFromDetails(updatedSquad);
      }

      setStatus('Saved and calendar updated.');
    } catch (e: any) {
      setStatus(String(e?.message || 'Failed to save.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ width: '100%', display: 'grid', gap: 10 }}>
      <div style={{ display: 'grid', gap: 8 }}>
        <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
          <div style={{ fontWeight: 600 }}>Select lead teacher</div>
          <SearchableSelect
            valueId={selectedTeacherId || ''}
            placeholder="Search teachers..."
            options={teacherOptions}
            disabled={loadingTeachers}
            onSelectId={(id) => setSelectedTeacherId(id)}
            showIdInMenu={false}
          />
        </label>

        {selectedTeacher && (
          <div style={{ padding: 8, background: '#f3f4f6', borderRadius: 6, fontSize: 12 }}>
            <div><strong>Selected:</strong> {selectedTeacher.displayName || 'Unnamed'}</div>
            <div><strong>Email:</strong> {selectedTeacher.email || 'Not set'}</div>
            {selectedTeacher.phoneNumber && (
              <div><strong>Phone:</strong> {selectedTeacher.phoneNumber}</div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button 
            type="button" 
            className="mini-btn" 
            onClick={() => void saveSelection()} 
            disabled={saving || loadingTeachers}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
          {status && (
            <div className="muted" style={{ fontSize: 12 }}>
              {status}
            </div>
          )}
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmState.open}
        onClose={() => {
          setConfirmState((s) => ({ ...s, open: false }));
          const r = confirmResolverRef.current;
          confirmResolverRef.current = null;
          r?.(false);
        }}
        onConfirm={() => {
          setConfirmState((s) => ({ ...s, open: false }));
          const r = confirmResolverRef.current;
          confirmResolverRef.current = null;
          r?.(true);
        }}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText="Cancel"
      />
    </div>
  );
}
